// Renders CMS content blocks for both the public page and the admin live preview.
export const BLOCK_LABELS = {
  hero: "Hero", text: "Text", image: "Image", image_text: "Image + Text",
  cards: "Cards", cta: "Call To Action", faq: "FAQ", gallery: "Gallery",
};

const Block = ({ block }) => {
  const p = block.props || {};
  switch (block.type) {
    case "hero":
      return (
        <section className="relative min-h-[340px] flex items-center overflow-hidden bg-[#12190f]">
          {p.image && <img src={p.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" />}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d1410] via-[#0d1410]/70 to-transparent" />
          <div className="relative max-w-4xl mx-auto px-6 py-16 text-bone">
            {p.eyebrow && <span className="hr-overline text-blaze">{p.eyebrow}</span>}
            <h1 className="font-serif text-4xl sm:text-6xl leading-[0.95] mt-2">{p.heading}</h1>
            {p.subheading && <p className="text-bone/75 text-lg mt-4 max-w-xl">{p.subheading}</p>}
            {p.ctaLabel && <a href={p.ctaHref || "#"} className="inline-block mt-6 bg-blaze text-white px-6 py-3 rounded-md font-semibold">{p.ctaLabel}</a>}
          </div>
        </section>
      );
    case "text":
      return (
        <section className="max-w-3xl mx-auto px-6 py-12">
          {p.heading && <h2 className="font-serif text-3xl text-charcoal mb-4">{p.heading}</h2>}
          <div className="text-charcoal/80 leading-relaxed whitespace-pre-wrap">{p.body}</div>
        </section>
      );
    case "image":
      return (
        <section className="max-w-4xl mx-auto px-6 py-8">
          {p.image && <img src={p.image} alt={p.caption || ""} className="w-full rounded-lg" />}
          {p.caption && <p className="text-sm text-charcoal/50 mt-2 text-center">{p.caption}</p>}
        </section>
      );
    case "image_text":
      return (
        <section className={`max-w-5xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-8 items-center ${p.imageRight ? "md:[&>*:first-child]:order-2" : ""}`}>
          {p.image && <img src={p.image} alt="" className="w-full rounded-lg" />}
          <div>
            {p.heading && <h2 className="font-serif text-3xl text-charcoal mb-3">{p.heading}</h2>}
            <div className="text-charcoal/80 leading-relaxed whitespace-pre-wrap">{p.body}</div>
          </div>
        </section>
      );
    case "cards":
      return (
        <section className="max-w-6xl mx-auto px-6 py-12">
          {p.heading && <h2 className="font-serif text-3xl text-charcoal mb-6">{p.heading}</h2>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(p.items || []).map((it, i) => (
              <div key={i} className="border border-charcoal/12 rounded-lg overflow-hidden">
                {it.image && <img src={it.image} alt="" className="w-full h-40 object-cover" />}
                <div className="p-4">
                  <h3 className="font-serif text-xl text-charcoal">{it.title}</h3>
                  <p className="text-sm text-charcoal/70 mt-1">{it.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      );
    case "cta":
      return (
        <section className="max-w-4xl mx-auto px-6 py-14 text-center">
          <h2 className="font-serif text-4xl text-charcoal">{p.heading}</h2>
          {p.body && <p className="text-charcoal/70 mt-3 max-w-xl mx-auto">{p.body}</p>}
          {p.ctaLabel && <a href={p.ctaHref || "#"} className="inline-block mt-6 bg-blaze text-white px-7 py-3 rounded-md font-semibold">{p.ctaLabel}</a>}
        </section>
      );
    case "faq":
      return (
        <section className="max-w-3xl mx-auto px-6 py-12">
          {p.heading && <h2 className="font-serif text-3xl text-charcoal mb-5">{p.heading}</h2>}
          <div className="space-y-4">
            {(p.items || []).map((it, i) => (
              <div key={i} className="border-b border-charcoal/12 pb-4">
                <p className="font-semibold text-charcoal">{it.q}</p>
                <p className="text-charcoal/70 mt-1">{it.a}</p>
              </div>
            ))}
          </div>
        </section>
      );
    case "gallery":
      return (
        <section className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-3 gap-3">
          {(p.images || []).map((src, i) => <img key={i} src={src} alt="" className="w-full h-48 object-cover rounded-md" />)}
        </section>
      );
    default:
      return <section className="max-w-3xl mx-auto px-6 py-6 text-charcoal/40 text-sm">Unknown block: {block.type}</section>;
  }
};

export function BlockRenderer({ sections }) {
  return (
    <div data-testid="cms-blocks">
      {(sections || []).filter((s) => !s.hidden).map((s) => <Block key={s.id} block={s} />)}
    </div>
  );
}
